import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import icpCampaignService, {
  IcpCampaign,
  IcpCampaignProspect,
  ProposedFilter,
  LemlistFilter,
  CompanyResult,
} from '../services/icpCampaignService';
import gtmService, { Icp, Persona, Offer } from '../services/gtmService';
import sequenceService, { Sequence } from '../services/sequenceService';
import { leadMagnetApi, LeadMagnet } from '../services/api';
import { PageHeader, useConfirm, RelatedLinks, type RelatedLinkItem } from '../components/ui';
import { gtmPfad } from '../vertrieb/gtmLinks';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { RechteHinweis } from '../vertrieb/RechteHinweis';

// A draft filter row in the editor (in-values held as a comma string for editing).
interface FilterRow {
  filterId: string;
  inText: string;
}

/*
 * Welle 2: Jeder Schritt ausser dem Lesen ist ein Schreibaufruf
 * (vertrieb.bearbeiten), das Uebertragen an Lemlist verlangt
 * versand.ausfuehren. Lead-Magnete (Inhalte-Router) und Sequenzen
 * (Versand-Router) laedt die Seite nur mit inhalte.lesen bzw. versand.lesen.
 * Lesende (Buchhaltung) sehen Setup, Prospects und Stand, aber keine Knoepfe.
 */
const IcpCampaignWizardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const darfUebertragen = darf('versand.ausfuehren');
  const siehtLeadMagnete = darf('inhalte.lesen');
  const siehtSequenzen = darf('versand.lesen');

  const [campaign, setCampaign] = useState<IcpCampaign | null>(null);
  const [prospects, setProspects] = useState<IcpCampaignProspect[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // which action is running

  // Asset pickers
  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [leadMagnets, setLeadMagnets] = useState<LeadMagnet[]>([]);
  const [sequences, setSequences] = useState<Sequence[]>([]);

  // Filter editor + search
  const [filterRows, setFilterRows] = useState<FilterRow[]>([]);
  const [availableFilters, setAvailableFilters] = useState<LemlistFilter[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [companies, setCompanies] = useState<CompanyResult[]>([]);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    const c = await icpCampaignService.get(id);
    setCampaign(c);
    setProspects(c.prospects || []);
    if (c.searchTerm) setSearchTerm(c.searchTerm);
    return c;
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [c, i, p, o, lm, sq] = await Promise.all([
          icpCampaignService.get(id as string),
          gtmService.getIcps(),
          gtmService.getPersonas(),
          gtmService.getOffers(),
          siehtLeadMagnete ? leadMagnetApi.getAll() : Promise.resolve([] as LeadMagnet[]),
          siehtSequenzen ? sequenceService.list() : Promise.resolve([] as Sequence[]),
        ]);
        setCampaign(c);
        setProspects(c.prospects || []);
        if (c.searchTerm) setSearchTerm(c.searchTerm);
        setIcps(i);
        setPersonas(p);
        setOffers(o);
        setLeadMagnets(lm);
        setSequences(sq);
      } catch (e: any) {
        setError(e?.response?.data?.error || e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-poll while any prospect is still enriching.
  useEffect(() => {
    // Abfragen ist ein POST (poll-enrichments): nur mit Schreibrecht.
    const stillEnriching = prospects.some((p) => p.enrichStatus === 'enriching');
    if (!stillEnriching || !id || !darfBearbeiten) {
      if (pollTimer.current) clearTimeout(pollTimer.current);
      return;
    }
    pollTimer.current = setTimeout(async () => {
      try {
        await icpCampaignService.pollEnrichments(id);
        await refresh();
      } catch {
        /* keep trying on next tick */
      }
    }, 4000);
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [prospects, id, refresh, darfBearbeiten]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  const patch = (data: Partial<IcpCampaign>) =>
    run('save', async () => {
      const updated = await icpCampaignService.update(id as string, data as any);
      setCampaign((c) => ({ ...(c as IcpCampaign), ...updated }));
      setNotice('Gespeichert.');
    });

  const handleProposeFilters = () =>
    run('filters', async () => {
      const { proposals, availableFilters: avail } = await icpCampaignService.proposeFilters(id as string);
      setAvailableFilters(avail);
      setFilterRows(
        (proposals.length ? proposals : ([] as ProposedFilter[])).map((p) => ({
          filterId: p.filterId,
          inText: p.in.join(', '),
        }))
      );
      if (!proposals.length) setNotice('Keine automatischen Vorschläge — bitte Filter manuell wählen.');
    });

  const handleSearch = () =>
    run('search', async () => {
      const filters = filterRows
        .filter((r) => r.filterId)
        .map((r) => ({
          filterId: r.filterId,
          in: r.inText.split(',').map((s) => s.trim()).filter(Boolean),
        }));
      if (!filters.length && !searchTerm.trim()) {
        throw new Error('Bitte mindestens einen Filter oder einen Suchbegriff angeben.');
      }
      const res = await icpCampaignService.search(id as string, { filters, search: searchTerm || undefined });
      await refresh();
      setNotice(`${res.created} neue Prospects (von ${res.totalAvailable} verfügbar).`);
    });

  // Optional account-discovery: find companies matching the ICP, then click one
  // to scope the people search (Step 3) to that company name.
  const handleSearchCompanies = () =>
    run('companies', async () => {
      const { proposals } = await icpCampaignService.proposeCompanyFilters(id as string);
      const filters = proposals.map((p) => ({ filterId: p.filterId, in: p.in }));
      if (!filters.length && !searchTerm.trim()) {
        throw new Error('Keine Konten-Filter ableitbar — ICP-Geografie/Branche pflegen oder Suchbegriff setzen.');
      }
      const res = await icpCampaignService.searchCompanies(id as string, { filters, search: searchTerm || undefined });
      setCompanies(res.companies);
      setNotice(
        res.companies.length
          ? `${res.companies.length} Firmen gefunden (von ${res.totalAvailable}). Auf eine Firma klicken, um die Personensuche darauf einzugrenzen.`
          : 'Keine Firmen gefunden — ICP-Geografie/Branche prüfen.'
      );
    });

  const handleEnrich = () =>
    run('enrich', async () => {
      const ids = Object.keys(selected).filter((k) => selected[k]);
      const res = await icpCampaignService.enrich(id as string, ids.length ? ids : undefined);
      await refresh();
      setNotice(`Anreicherung gestartet für ${res.started} Prospects.`);
    });

  const handleImport = () =>
    run('import', async () => {
      const res = await icpCampaignService.importLeads(id as string);
      await refresh();
      setNotice(`${res.imported} Leads importiert (${res.skipped} übersprungen).`);
    });

  const handleIcebreakers = () =>
    run('icebreakers', async () => {
      const res = await icpCampaignService.generateIcebreakers(id as string);
      await refresh();
      setNotice(`Icebreaker generiert: ${res.generated} (${res.failed} fehlgeschlagen).`);
    });

  const handlePush = () =>
    run('push', async () => {
      const ok = await confirm({
        title: 'An Lemlist übertragen?',
        message: 'Sequenz und importierte Leads werden als echte Lemlist-Kampagne angelegt und können dort versendet werden. Rückgängig nur durch Löschen der Kampagne in Lemlist.',
        confirmLabel: 'Übertragen',
      });
      if (!ok) return;
      const res = await icpCampaignService.push(id as string);
      await refresh();
      setNotice(`An Lemlist übertragen — ${res.synced} Leads synchronisiert (${res.failed} fehlgeschlagen).`);
    });

  if (!campaign) {
    return (
      <div className="page-container-wide">
        {error ? <div className="alert alert-error">{error}</div> : <p className="text-muted">Lade…</p>}
      </div>
    );
  }

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedCount = selectedIds.length;
  const allSelected = prospects.length > 0 && selectedCount === prospects.length;
  const toggleAll = (checked: boolean) => {
    const next: Record<string, boolean> = {};
    if (checked) prospects.forEach((p) => (next[p.id] = true));
    setSelected(next);
  };

  const enrichingCount = prospects.filter((p) => p.enrichStatus === 'enriching').length;
  const deliverableCount = prospects.filter(
    (p) => p.email && ['deliverable', 'risky', 'valid', 'ok'].includes((p.emailStatus || '').toLowerCase())
  ).length;
  const importedCount = prospects.filter((p) => p.leadId).length;
  const personaOptions = personas.filter((p) => !campaign.icpId || !p.icp || p.icp.id === campaign.icpId);
  const offerOptions = offers.filter((o) => !campaign.icpId || !o.icp || o.icp.id === campaign.icpId);

  return (
    <div className="page-container-wide">
      <PageHeader
        eyebrow={(
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/icp-campaigns')}>
            ← Alle Kampagnen nach Zielkundenprofil (ICP)
          </button>
        )}
        title={campaign.name}
        subtitle={(
          <>
            Status: <span className="badge">{campaign.status}</span>
          </>
        )}
      />

      <RechteHinweis was="ICP-Kampagne einrichten, suchen, anreichern und importieren" />

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>}
      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-4)' }}>{notice}</div>}

      {/* 1) Setup */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 className="card-title">1 · Setup</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <Picker label="ICP" value={campaign.icpId} onChange={(v) => patch({ icpId: v })} options={icps} disabled={!darfBearbeiten} />
          <Picker label="Persona" value={campaign.personaId} onChange={(v) => patch({ personaId: v })} options={personaOptions} disabled={!darfBearbeiten} />
          <Picker label="Offer" value={campaign.offerId} onChange={(v) => patch({ offerId: v })} options={offerOptions} disabled={!darfBearbeiten} />
          {siehtLeadMagnete && (
            <Picker label="Lead-Magnet" value={campaign.leadMagnetId} onChange={(v) => patch({ leadMagnetId: v })} options={leadMagnets} disabled={!darfBearbeiten} />
          )}
          {siehtSequenzen && (
            <Picker label="Sequenz" value={campaign.sequenceId} onChange={(v) => patch({ sequenceId: v })} options={sequences} disabled={!darfBearbeiten} />
          )}
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Ziel / CTA (optional)</label>
            <input
              className="form-input"
              defaultValue={campaign.goal || ''}
              disabled={!darfBearbeiten}
              onBlur={(e) => e.target.value !== (campaign.goal || '') && patch({ goal: e.target.value })}
            />
          </div>
        </div>
        {(() => {
          // Was oben gewaehlt ist, auch anspringbar machen.
          const name = <T extends { id: string; name: string }>(liste: T[], id?: string | null) => liste.find((x) => x.id === id)?.name;
          const items: RelatedLinkItem[] = [
            campaign.icpId && { key: 'icp', label: name(icps, campaign.icpId) ?? 'ICP', icon: 'target' as const, to: gtmPfad('icp', campaign.icpId), title: 'Zielkundenprofil öffnen' },
            campaign.personaId && { key: 'persona', label: name(personaOptions, campaign.personaId) ?? 'Persona', icon: 'user' as const, to: gtmPfad('persona', campaign.personaId), title: 'Persona öffnen' },
            campaign.offerId && { key: 'offer', label: name(offerOptions, campaign.offerId) ?? 'Offer', icon: 'briefcase' as const, to: gtmPfad('offer', campaign.offerId), title: 'Angebot (Offer) öffnen' },
            siehtLeadMagnete && campaign.leadMagnetId && { key: 'lm', label: name(leadMagnets, campaign.leadMagnetId) ?? 'Lead-Magnet', icon: 'magnet' as const, to: `/lead-magnets?id=${encodeURIComponent(campaign.leadMagnetId)}`, title: 'Lead-Magnet öffnen' },
            siehtSequenzen && campaign.sequenceId && { key: 'seq', label: name(sequences, campaign.sequenceId) ?? 'Sequenz', icon: 'workflow' as const, to: `/sequences?id=${encodeURIComponent(campaign.sequenceId)}`, title: 'Sequenz öffnen' },
            campaign.lemlistCampaignId && { key: 'leads', label: 'Leads der Kampagne', icon: 'users' as const, to: `/lead-sync?kampagne=${encodeURIComponent(campaign.lemlistCampaignId)}`, title: 'Übertragene Leads in Leads & Outreach' },
          ].filter(Boolean) as RelatedLinkItem[];
          return items.length > 0
            ? <RelatedLinks className="mt-3" items={items} label="Verknüpft mit dieser Kampagne" />
            : null;
        })()}
        <p className="text-xs text-muted" style={{ marginTop: 'var(--space-2)' }}>
          Lead-Magnet fließt als Kontext in die Icebreaker und — falls eine gehostete URL gesetzt ist — als{' '}
          <code>{'{{leadMagnet}}'}</code>-Link in die Sequenz ein.
        </p>
      </div>

      {/* 2) Filter -- nur sinnvoll, wenn danach gesucht werden darf (POST). */}
      {darfBearbeiten && (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <h3 className="card-title">2 · Filter aus ICP ableiten & prüfen</h3>
          <button className="btn btn-secondary" onClick={handleProposeFilters} disabled={busy === 'filters'}>
            {busy === 'filters' ? 'Lade…' : 'Filter vorschlagen'}
          </button>
          {filterRows.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {filterRows.map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <select
                    className="form-select"
                    style={{ maxWidth: '220px' }}
                    value={row.filterId}
                    onChange={(e) => setFilterRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, filterId: e.target.value } : r)))}
                  >
                    <option value="">Filter wählen…</option>
                    {availableFilters.map((f) => (
                      <option key={f.filterId} value={f.filterId}>
                        {f.filterId}{f.description ? ` — ${f.description}` : ''}
                      </option>
                    ))}
                  </select>
                  <input
                    className="form-input"
                    style={{ flex: 1 }}
                    placeholder="Werte, kommagetrennt (z. B. France, Germany)"
                    value={row.inText}
                    onChange={(e) => setFilterRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, inText: e.target.value } : r)))}
                  />
                  <button className="btn btn-ghost btn-sm" onClick={() => setFilterRows((rows) => rows.filter((_, idx) => idx !== i))}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end', marginTop: 'var(--space-3)' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setFilterRows((rows) => [...rows, { filterId: '', inText: '' }])}
            >
              + Filter
            </button>
            <div className="form-group" style={{ flex: 1, margin: 0 }}>
              <label className="form-label">Freitext-Suche (optional)</label>
              <input className="form-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {/* 3) Search + select */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 className="card-title">3 · Lemlist-Suche & Auswahl</h3>

        {/* Optional account-based discovery: find matching companies first. */}
        {darfBearbeiten && (
          <details style={{ marginBottom: 'var(--space-3)' }}>
            <summary style={{ cursor: 'pointer', fontSize: '13px', color: 'var(--color-text-muted, #64748b)' }}>
              Konten-Discovery (optional) — passende Firmen aus dem ICP finden
            </summary>
            <div style={{ marginTop: 'var(--space-2)' }}>
              <button className="btn btn-secondary btn-sm" onClick={handleSearchCompanies} disabled={busy === 'companies'}>
                {busy === 'companies' ? 'Suche Firmen…' : 'Passende Firmen finden'}
              </button>
              {companies.length > 0 && (
                <div className="table-container" style={{ maxHeight: '28vh', overflowY: 'auto', marginTop: 'var(--space-2)' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Firma</th>
                        <th>Branche</th>
                        <th>Land</th>
                        <th>Größe</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map((c, i) => (
                        <tr key={(c.companyId || c.name || '') + i}>
                          <td className="text-xs">
                            {c.name ? <Link to={`/companies?suche=${encodeURIComponent(c.name)}`} title="Im CRM suchen">{c.name}</Link> : '—'}
                          </td>
                          <td className="text-xs">{c.industry || '—'}</td>
                          <td className="text-xs">{c.country || c.location || '—'}</td>
                          <td className="text-xs">{c.size || '—'}</td>
                          <td>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                if (c.name) setSearchTerm(c.name);
                                setNotice(`Personensuche auf „${c.name}" eingegrenzt — jetzt „In Lemlist suchen".`);
                              }}
                              disabled={!c.name}
                              title="Personensuche auf diese Firma eingrenzen"
                            >
                              Auf Personen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </details>
        )}

        {darfBearbeiten && (
          <button className="btn btn-primary" onClick={handleSearch} disabled={busy === 'search'}>
            {busy === 'search' ? 'Suche…' : 'In Lemlist suchen'}
          </button>
        )}
        {!darfBearbeiten && prospects.length === 0 && <p className="text-sm text-muted">Noch keine Prospects gesucht.</p>}
        {prospects.length > 0 && (
          <div className="table-container" style={{ maxHeight: '40vh', overflowY: 'auto', marginTop: 'var(--space-3)' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={!darfBearbeiten}
                      onChange={(e) => toggleAll(e.target.checked)}
                      title="Alle aus-/abwählen"
                    />
                  </th>
                  <th>Name</th>
                  <th>Titel</th>
                  <th>Firma</th>
                  <th>Land</th>
                  <th>Score</th>
                  <th>E-Mail</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {prospects.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selected[p.id]}
                        disabled={!darfBearbeiten}
                        onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))}
                      />
                    </td>
                    <td>
                      {p.leadId
                        ? <Link to={`/lead-sync?lead=${encodeURIComponent(p.leadId)}`} title="Lead öffnen">{p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ') || 'Lead'}</Link>
                        : (p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ') || '—')}
                    </td>
                    <td className="text-xs">{p.title || '—'}</td>
                    <td className="text-xs">
                      {p.company ? <Link to={`/companies?suche=${encodeURIComponent(p.company)}`} title="Im CRM suchen">{p.company}</Link> : '—'}
                    </td>
                    <td className="text-xs">{p.country || '—'}</td>
                    <td>{p.qualityScore ?? '—'}</td>
                    <td className="text-xs">
                      {p.email ? `${p.email} (${p.emailStatus || '?'})` : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <span className="badge">{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4) Enrich */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 className="card-title">4 · E-Mails anreichern</h3>
        <p className="text-xs text-muted">
          Lemlist liefert bei der Suche keine E-Mails. Die Anreicherung läuft asynchron und verbraucht Lemlist-Credits.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-2)' }}>
          {darfBearbeiten && (
            <button className="btn btn-primary" onClick={handleEnrich} disabled={busy === 'enrich' || prospects.length === 0}>
              {busy === 'enrich'
                ? 'Starte…'
                : selectedCount > 0
                ? `${selectedCount} ausgewählte anreichern`
                : 'Alle gefundenen anreichern'}
            </button>
          )}
          {enrichingCount > 0 && <span className="text-sm">⏳ {enrichingCount} in Anreicherung…{darfBearbeiten ? ' (aktualisiert automatisch)' : ''}</span>}
          {enrichingCount === 0 && deliverableCount > 0 && (
            <span className="text-sm">✓ {deliverableCount} mit zustellbarer E-Mail</span>
          )}
        </div>
      </div>

      {/* 5) Import */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 className="card-title">5 · Als Leads importieren</h3>
        {darfBearbeiten && (
          <button className="btn btn-primary" onClick={handleImport} disabled={busy === 'import' || deliverableCount === 0}>
            {busy === 'import' ? 'Importiere…' : `${deliverableCount} zustellbare als Leads importieren`}
          </button>
        )}
        {importedCount > 0 && <span className="text-sm" style={{ marginLeft: 'var(--space-2)' }}>✓ {importedCount} importiert</span>}
        {!darfBearbeiten && importedCount === 0 && <span className="text-sm text-muted">Noch nichts importiert.</span>}
      </div>

      {/* 6) Icebreakers */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 className="card-title">6 · Icebreaker generieren</h3>
        {darfBearbeiten && (
          <button className="btn btn-primary" onClick={handleIcebreakers} disabled={busy === 'icebreakers' || importedCount === 0}>
            {busy === 'icebreakers' ? 'Generiere…' : 'Icebreaker für importierte Leads'}
          </button>
        )}
      </div>

      {/* 7) Push */}
      <div className="card">
        <h3 className="card-title">7 · An Lemlist übertragen</h3>
        {darfUebertragen && !campaign.sequenceId && <p className="text-xs text-muted">Bitte oben eine Sequenz auswählen.</p>}
        {campaign.lemlistCampaignId ? (
          <div className="alert alert-info">
            Übertragen an Lemlist-Kampagne <code>{campaign.lemlistCampaignId}</code>.{' '}
            <Link to={`/lead-sync?kampagne=${encodeURIComponent(campaign.lemlistCampaignId)}`}>Leads ansehen</Link>
          </div>
        ) : darfUebertragen ? (
          <button
            className="btn btn-primary"
            onClick={handlePush}
            disabled={busy === 'push' || importedCount === 0 || !campaign.sequenceId}
          >
            {busy === 'push' ? 'Übertrage…' : 'Sequenz + Leads übertragen'}
          </button>
        ) : (
          <RechteHinweis recht="versand.ausfuehren" was="Noch nicht übertragen. Übertragen an Lemlist" className="text-xs text-muted" />
        )}
      </div>
    </div>
  );
};

// Small reusable asset picker (id/name options + "none").
const Picker: React.FC<{
  label: string;
  value?: string | null;
  onChange: (v: string) => void;
  options: { id: string; name: string }[];
  disabled?: boolean;
}> = ({ label, value, onChange, options, disabled }) => (
  <div className="form-group" style={{ margin: 0 }}>
    <label className="form-label">{label}</label>
    <select className="form-select" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">— keine —</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  </div>
);

export default IcpCampaignWizardPage;
